import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import clsx from 'clsx'

export interface CheckboxDropdownOption {
  value: string
  label: string
}

export interface CheckboxDropdownProps {
  id?: string
  /** Currently-checked values. Empty means "nothing filtered - show all". */
  values: string[]
  onChange: (values: string[]) => void
  options: CheckboxDropdownOption[]
  /** Shown on the closed button when `values` is empty. */
  placeholder: string
  className?: string
}

const VIEWPORT_MARGIN = 8
const HIDDEN_STYLE: CSSProperties = { position: 'fixed', top: 0, left: 0, visibility: 'hidden' }

// Multi-select variant of Select.tsx - same measured `position: fixed`
// positioning technique (escapes any ancestor's overflow clipping) and the
// same click-outside-to-close behaviour, but toggles membership in a list
// instead of replacing a single value, and stays open after a click so the
// operator can check several options in one pass (closed only by outside
// click, Escape, or the button itself).
export function CheckboxDropdown({ id, values, onChange, options, placeholder, className }: CheckboxDropdownProps) {
  const [open, setOpen] = useState(false)
  const [style, setStyle] = useState<CSSProperties>(HIDDEN_STYLE)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const listboxId = useId()

  const buttonLabel =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? (options.find((option) => option.value === values[0])?.label ?? placeholder)
        : `${values.length} selected`

  useLayoutEffect(() => {
    if (!open) {
      setStyle(HIDDEN_STYLE)
      return
    }
    const button = buttonRef.current
    if (!button) return
    const rect = button.getBoundingClientRect()
    const maxLeft = window.innerWidth - rect.width - VIEWPORT_MARGIN
    setStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: Math.min(rect.left, Math.max(maxLeft, VIEWPORT_MARGIN)),
      width: rect.width,
      visibility: 'visible',
    })
  }, [open])

  useEffect(() => {
    if (!open) return
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (buttonRef.current?.contains(target) || listRef.current?.contains(target)) return
      setOpen(false)
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  function toggleValue(value: string) {
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value])
  }

  return (
    <div className={clsx('relative', className)}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-2 rounded-control border border-border bg-bg px-3 py-2 text-left text-body text-text-primary outline-none"
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronDown size={16} strokeWidth={1.5} className="shrink-0 text-text-secondary" />
      </button>
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-multiselectable="true"
          style={style}
          className="z-50 max-h-64 overflow-y-auto rounded-control border border-border bg-surface-raised py-1 shadow-lg"
        >
          {values.length > 0 && (
            <li className="border-b border-border px-3 py-2">
              <button
                type="button"
                onClick={() => onChange([])}
                className="text-meta font-medium text-accent hover:underline"
              >
                Clear
              </button>
            </li>
          )}
          {options.map((option) => {
            const checked = values.includes(option.value)
            return (
              <li
                key={option.value}
                role="option"
                aria-selected={checked}
                onClick={() => toggleValue(option.value)}
                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-body text-text-secondary hover:bg-surface"
              >
                <span
                  className={clsx(
                    'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
                    checked ? 'border-accent bg-accent' : 'border-border',
                  )}
                >
                  {checked && <Check size={12} strokeWidth={2.5} className="text-white" />}
                </span>
                <span className="truncate">{option.label}</span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
